import { Component } from '@angular/core';
import { OnDestroy, AfterViewInit, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { AlertsService } from '../../services/alerts.service';
import { ProductService } from '../../services/product.service';
import { ViewportService } from '../../services/viewport.service';
import { MapService } from '../../map/services/map.service';
import { formatString } from '../../utils/string-utils';

import { AgGridAngular } from 'ag-grid-angular';
import { GridOptions, SortChangedEvent, PaginationChangedEvent, Column } from 'ag-grid-community';
import { GridCellRendererComponent } from '../grid-cell-renderer/grid-cell-renderer.component';
import { GridGeoFeatureToolsCellRendererComponent } from '../grid-geo-feature-tools-cell-renderer/grid-geo-feature-tools-cell-renderer.component';

import { BaseMap, MapControl } from '../../models/map.interface';

import { loadModules } from 'esri-loader';

declare let Terraformer: any; // terraformer-wkt-parser.js, https://github.com/esri/terraformer-wkt-parser


/**
 * Provides a view of a geographic product.
 */
@Component({
	selector: 'doc-geo-view',
	templateUrl: './doc-geo-view.component.html',
	styleUrl: './doc-geo-view.component.css'
})
export class DocGeoViewComponent implements OnDestroy, AfterViewInit {

	@Input() product!: any;
	@Input() documentData!: { [key: string]: any };
	@Input() viewerURL: SafeResourceUrl | undefined;
	@Input() viewType!: string;
	@Input() projectedPageNumber: number = 0;
	
	@Output() triggerExitProjection = new EventEmitter<{ pageNumber: number }>();

	@ViewChild('mapContainer') mapContainer!: ElementRef;
	@ViewChild('kvGrid') grid!: AgGridAngular;

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};

	mapId: string = 'arcgis-layer';
	mapView: any;
	basemapConfigs: BaseMap[] = [];

	mapControl!: MapControl;
	featuresToggled: boolean = false;

	arcgis = {
		type: 'geojson',
		pagination: {
			total: 0,
			page: 1,
			num_per_page: 100,
			pagesize: [25, 50, 100, 1000]
		},
		pageChanged: (newPage: any) => {
			this.arcgis.pagination.page = newPage;
			this.getFeatures();
		}
	};
	geoJsonLayer: any;
	loading: string = '';


	// Features grid
	// Properties applied to all columns
	defaultColDef = {
		flex: 1
	};

	gridOptions: GridOptions = {
		context: {
			zoomToFeature: this.zoomToFeature.bind(this)
		}
	};

	columns: any[] = [];
	gridData: any[] = [];


	// Projection
	imageElement: any;
	projectionvisible: boolean = true;
	opacity: number = 1;

	constructor(
		private userService: UserService,
		private configService: ConfigService,
		private alertsService: AlertsService,
		private productService: ProductService,
		private viewportService: ViewportService,
		private mapService: MapService
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();

		this.basemapConfigs = this.configService.getConfig()?.esri?.basemaps || [];
		this.mapControl = this.mapService.getMapControl();
	}

	ngAfterViewInit(): void {
		if (!this.viewerURL) {
			this.loading = 'ERROR';
			return;
		}

		this.initializeMap();
	}

	ngOnDestroy() {
		if (this.mapView) {
			this.mapView.destroy();
		}
	}

	/**
	 * Reinitialize this component without destroying the DOM.
	 */
	ngOnChanges(changes: SimpleChanges) {
		if (this.mapView && changes['product']) {

			this.mapView.container = null; // Detach from DOM
			this.mapView.destroy();        // Fully tear down
			this.mapView = null;
			this.initializeMap();
		}
	}

	private async initializeMap(): Promise<any> {
		this.mapView = await this.loadMap();
		this.mapService.loadBaseMap(this.mapView);

		this.mapView.when(() => {
			console.log("%s view is ready. All layers loaded.", this.mapId);

			let coords = ["-116.844", "33.032"];
			let zoom = this.viewType === 'map' ? 10 : this.configService.getConfig().esri.zoom;
			this.mapService.centerAndZoomMap(this.mapView, coords, zoom);

			if (this.viewType === 'arcgis') {
				this.onFeatureMapLoaded();
			} else if (this.viewType === 'map') {
				this.onProjectionMapLoaded();
			}
		});
	}

	private async loadMap(): Promise<any> {
		try {
			const [Map, MapView] = await loadModules(['esri/Map', 'esri/views/MapView']);
			const map = new Map({
				basemap: 'topo-vector'
			});

			const view = new MapView({
				container: this.mapContainer.nativeElement,
				map: map,
				center: [-118, 34],
				zoom: 10
			});

			return view;
		} catch (err) {
			console.error("ArcGIS API load error: ", err)
			return undefined;
		}
	}

	private async onFeatureMapLoaded(): Promise<any> {
		this.displayFeatures();
	}

	/**
	 * Loads the record's geographic features.
	 */
	async displayFeatures(): Promise<void> {
		let countURL = this.documentData['arcGISQueryURL'] + "?returnCountOnly=" + true;
		let response = await this.mapService.getFeaturesCount(countURL);
		if (response.properties && response.properties.count && response.properties.count >= 0) {
			this.arcgis.type = "geojson";
			this.arcgis.pagination.total = response.properties.count;
			this.arcgis.pagination.page = 1;

			this.getFeatures();
		} else {
			this.arcgis.type = "geojson";
			this.loadFeatures(countURL, response);
		}
	}

	changeBasemap(): void {
		this.mapService.loadBaseMap(this.mapView, this.mapControl.baseMapUnit);
	}

	toggleFeatures(): void {
		this.featuresToggled = !this.featuresToggled;
	}

	pageSizeChanged(pageSize: number): void {
		this.arcgis.pagination.num_per_page = pageSize;
		this.arcgis.pageChanged(1);
	}

	getFeatures(): void {
		if (!this.documentData['arcGISQueryURL']) return;

		this.loading = "PENDING";

		let offset = (this.arcgis.pagination.num_per_page * (this.arcgis.pagination.page - 1));
		let url = this.documentData['arcGISQueryURL'];
		url += `?resultRecordCount=${this.arcgis.pagination.num_per_page}`;
		url += `&resultOffset=${offset}`;

		this.mapService.getFeatures(url).then(
			(data: any) => {
				this.loadFeatures(url, data);
			},
			(err: any) => {
				console.error("Failed to load features: ", err);
				this.loading = "ERROR";
			}
		);
	}

	private loadFeatures(url: string, data: any): void {
		this.buildGridColumns(data);
		this.loadDataGrid(data);
		this.loadFeatureMapLayer(url, data);
	}

	private buildGridColumns(data: any): void {
		this.columns = [];

		if (data?.features?.length > 0) {
			// -- Zoom to Feature column --
			this.columns.push({
				headerName: '',
				colId: '_buttons',
				cellRenderer: GridGeoFeatureToolsCellRendererComponent,
				hide: false,
				width: 65,
				suppressMenu: true,
				suppressSorting: true,
				pinned: 'left'
			});

			Object.entries(data.features[0].properties).forEach(([key, value]) => {
				let def = {
					field: key,
					hide: false,
					autoHeight: false,
					wrapText: false,
					suppressNavigable: true,
					suppressSorting: true,
					headerName: (key || 'Feature Property'),
					cellRenderer: GridCellRendererComponent as any
				};

				this.columns.push(def);
			});
		}
	}

	private loadDataGrid(data: any): void {
		this.gridData = [];

		if (data?.features?.length > 0) {
			data.features.forEach((feature: any) => {
				let attrs = feature.properties;
				attrs.geometry = feature.geometry;
				this.gridData.push(attrs);
			});
		}

		this.loading = "DONE";
	}

	// This is called from the Features data grid.
	// 'feature' is a JSON object.
	zoomToFeature(feature: any): void {
		let geo = Terraformer.WKT.convert(feature.geometry);
		this.mapService.getGeometryFromWKT(geo).then(
			(point: any) => {
				this.mapView.goTo({
					target: point,
					zoom: 15
				});
			},
			(err: any) => {
				console.error("Failed to get feature geometry: ", err);
			}
		);
	}

	private loadFeatureMapLayer(url: string, data: any): void {
		if (this.geoJsonLayer) {
			if (this.mapView.map.findLayerById(this.geoJsonLayer.id)) {
				this.mapView.map.remove(this.geoJsonLayer);
			}
		}

		this.mapService.loadFeatureMapLayer(url, data, {}).then(
			(layer: any) => {
				this.geoJsonLayer = layer;
				this.mapView.map.add(this.geoJsonLayer);

				console.log("Added %s layer with id %s", layer.type, layer.id);

				this.geoJsonLayer.when(() => {
					if (this.geoJsonLayer.fullExtent) {
						this.mapView.goTo(this.geoJsonLayer.fullExtent.expand(1.25))
							.catch((error: any) => {
								console.error("Error setting extent: ", error);
							});
					}
				}).catch((error: any) => {
					console.error("Error loading GeoJSONLayer: ", error);
				})
			},
			(err: any) => {
				console.error("Failed to load feature map layer: ", err);
			}
		);
	}

	private async onProjectionMapLoaded(): Promise<any> {
		this.projectionvisible = true;
		this.opacity = 1;

		if (this.viewType === 'map') {
			// GEOTIFF, GEOPDF (user toggled to display projected image from a separate viewer)
			this.loadGeoProjection();
		}
		else {
			// Auto-loads the KML features
			this.loadKMLFeatures();
		}
	}

	private async loadKMLFeatures(): Promise<any> {
		const [
			KMLLayer,
			FeatureLayer,
			Extent,
			SpatialReference,
			Graphic,
			InfoTemplate,
			geometryEngine,
			webMercatorUtils
		] = await loadModules([
			'esri/layers/KMLLayer',
			'esri/layers/FeatureLayer',
			'esri/geometry/Extent',
			'esri/geometry/SpatialReference',
			'esri/graphic',
			'esri/InfoTemplate',
			'esri/geometry/geometryEngine',
			'esri/geometry/webMercatorUtils'
		]);

		let kmlTemplate = new InfoTemplate();

		function getKMLContent(graphic: any): string {
			kmlTemplate.setTitle(graphic.attributes.name ? "${name}" : "ID: ${id}");

			let content = "";
			for (const [key, value] of Object.entries(graphic.attributes)) {
				content += `<b>${key}:</b> ${value}<br/>`;
			}
			return content;
		}

		kmlTemplate.setContent(getKMLContent);

		try {
			let globalExtent = null;
			let response = await this.mapService.getKMLLayers(this.documentData['arcGISQueryURL']);
			let layers = response.featureCollection.layers;
			//let layers = new KMLLayer(location.origin + this.viewerURL);
			//let layers = new KMLLayer("http://geointdemo.cloudapp.net/download/autocad.kml"); // uncomment for kml testing
			for (let j = 0; j < layers.length; j++) {
				if (layers[j].featureSet && layers[j].featureSet.features) {
					let collection = {
						layerDefinition: layers[j].layerDefinition,
						featureSet: layers[j].featureSet,
					};
					let featureLayer = new FeatureLayer(collection);
					featureLayer.setInfoTemplate(kmlTemplate);

					const features = layers[j].featureSet.features;

					if (features && features.length > 0) {
						// Get array of geometries
						const geometries = features.map((f: any) => f.geometry);
						const unionExtent = geometryEngine.union(geometries)?.extent;

						if (unionExtent) {
							const lyrExtent = webMercatorUtils.geographicToWebMercator(unionExtent);
							if (globalExtent) {
								globalExtent = globalExtent.union(lyrExtent);
							} else {
								globalExtent = lyrExtent;
							}
						}
					}

					this.mapView.addLayer(featureLayer);
				}
			}

			if (globalExtent) {
				this.mapView.goTo(globalExtent.expand(1.25)).catch((error: any) => {
					console.error("Error setting extent: ", error);
				});
			}
		} catch (err) {
			this.loading = "ERROR";
			this.alertsService.error({
				title: "Failed to Load",
				message: "Document provider failed to load KML.",
				delay: 4000
			});
		}
	}

	async getMapLayer(id: string): Promise<any> {
		const [MediaLayer] = await loadModules(['esri/layers/MediaLayer']);

		let layer = this.mapView.map.findLayerById(id);
		if (layer) return layer;

		switch (id) {
			case "projection":
				layer = new MediaLayer({
					id: "projection",
					source: [],
					opacity: 0.9,
					blendMode: "normal"
				});
				break;
		}

		if (layer) {
			this.mapView.map.add(layer);
			console.log("Created new layer with id '%s'", layer.id);
		}
		return layer;
	}

	private async loadGeoProjection(): Promise<any> {
		const response = await this.mapService.getProjectedInfo(this.product.id, this.projectedPageNumber);
		if (!response) {
			console.error("Error retrieving projected information.");
			this.loading = 'ERROR';
			return;
		}

		const [
			ImageElement,
			ExtentAndRotationGeoreference,
			Extent
		] = await loadModules([
			"esri/layers/support/ImageElement",
			"esri/layers/support/ExtentAndRotationGeoreference",
			"esri/geometry/Extent"
		]);

		let layer = await this.getMapLayer("projection");

		let projectedJSON = this.mapService.getJSONfromXML(response);
		let url = projectedJSON.overlay_images.overlay.url;
		let bounds = projectedJSON.overlay_images.overlay.bounds;
		let extent = new Extent({
			xmin: bounds.west,
			ymin: bounds.south,
			xmax: bounds.east,
			ymax: bounds.north,
			spatialReference: { wkid: 4326 }
		});

		layer.spatialReference = extent.spatialReference;

		// The safest maximum image size is 2048 x 2048 pixels.
		this.imageElement = new ImageElement({
			image: url,
			georeference: new ExtentAndRotationGeoreference({
				extent: extent
			})
		});
		layer.source.elements.add(this.imageElement);

		this.mapView.goTo(extent).catch((error: any) => {
			console.error("Error setting extent: ", error);
		});
	}

	async toggleProjection(): Promise<void> {
		let layer = await this.getMapLayer("projection");
		layer.visible = !this.projectionvisible;
	}

	toggleOpacity(): void {
		this.imageElement.opacity = this.opacity;
		this.mapView.goTo(this.mapView.center, { animate: false }); // Jiggle the map to initiate a rerender
	}
	
	exitProjection(): void {
		let page = 0; // TODO do we need to pick up the current page (eg. NTF)?
		this.triggerExitProjection.emit({ pageNumber: page });
	}
}
